import styled from '@emotion/styled';

import { USER_ROLE_TO_NAME } from '@admin/constants/user_role';
import { AccessControlPermission, User__Role } from '@admin/schema';

export const CRUD_ORDER = ['read', 'create', 'update', 'delete'];

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

export const TabPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  &[hidden] {
    display: none;
  }
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: #1a1f36;
`;

export const Subtitle = styled.p`
  margin: 0;
  color: #5b6477;
  font-size: 15px;
`;

export const TabBar = styled.div`
  display: inline-flex;
  gap: 8px;
  padding: 4px;
  background: #f3f5f8;
  border-radius: 10px;
  width: fit-content;
`;

export const TabButton = styled.button<{ active: boolean }>`
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ active }) => (active ? '#ffffff' : 'transparent')};
  color: ${({ active }) => (active ? '#1a1f36' : '#5b6477')};
  box-shadow: ${({ active }) => (active ? '0 1px 3px rgba(16, 24, 40, 0.12)' : 'none')};
`;

export const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

export const SearchInput = styled.input`
  min-width: 280px;
  flex: 1;
  max-width: 420px;
  border: 1px solid #d7dde7;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 20px;
  min-height: 520px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const SelectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
`;

export const ExplorerSidebar = styled.div`
  border: 1px solid #e4e8ef;
  border-radius: 12px;
  background: #ffffff;
  max-height: 520px;
  overflow-y: auto;
`;

export const ExplorerResourceGroup = styled.div`
  & + & {
    border-top: 1px solid #e4e8ef;
  }
`;

export const ExplorerResourceHeader = styled.button<{ expanded: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: ${({ expanded }) => (expanded ? '#f8fafc' : '#ffffff')};
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  color: #1a1f36;
  text-align: left;
`;

export const ExplorerResourceMeta = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #5b6477;
`;

export const ExplorerPermissionList = styled.div`
  padding: 4px 8px 8px;
`;

export const ExplorerPermissionItem = styled.button<{ active: boolean }>`
  width: 100%;
  display: block;
  text-align: left;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  background: ${({ active }) => (active ? '#f5f7ff' : 'transparent')};

  &:hover {
    background: ${({ active }) => (active ? '#f5f7ff' : '#f8fafc')};
  }

  & + & {
    margin-top: 2px;
  }
`;

export const SelectionItem = styled.button<{ active: boolean }>`
  text-align: left;
  border: 1px solid ${({ active }) => (active ? '#4f6df5' : '#e4e8ef')};
  background: ${({ active }) => (active ? '#f5f7ff' : '#ffffff')};
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
`;

export const SelectionTitle = styled.div`
  font-weight: 600;
  color: #1a1f36;
`;

export const SelectionMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #5b6477;
  line-height: 1.4;
`;

export const ScrollableContent = styled.div`
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
`;

export const PermissionGroup = styled.div`
  border: 1px solid #e4e8ef;
  border-radius: 12px;
  overflow: hidden;

  & + & {
    margin-top: 12px;
  }
`;

export const PermissionGroupHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border: none;
  cursor: pointer;
  font-weight: 600;
  color: #1a1f36;
  text-align: left;
`;

export const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 12px 16px 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const RoleCheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const PermissionCard = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  cursor: pointer;
  background: #ffffff;
`;

export const PermissionTitle = styled.span`
  display: block;
  font-weight: 600;
  color: #1a1f36;
`;

export const ActionTitle = styled(PermissionTitle)`
  text-transform: capitalize;
`;

export const PermissionDescription = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #5b6477;
`;

export const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 20px;
`;

export const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: #5b6477;
`;

export const Badge = styled.span<{ tone?: 'neutral' | 'accent' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  background: ${({ tone }) => {
    if (tone === 'accent') return '#e8edff';
    if (tone === 'warning') return '#fff4e5';
    return '#eef2f7';
  }};
  color: ${({ tone }) => {
    if (tone === 'accent') return '#3b4cca';
    if (tone === 'warning') return '#5c3d00';
    return '#5b6477';
  }};
`;

export const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d7dde7;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 13px;
  color: #1a1f36;
  background: #ffffff;
`;

export const Section = styled.section`
  & + & {
    margin-top: 24px;
  }
`;

export const SectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1f36;
`;

export const DetailMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

export const UserAccessRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #eef2f7;

  &:last-child {
    border-bottom: none;
  }
`;

export const UserAccessInfo = styled.div`
  min-width: 0;
`;

export const UserAccessName = styled.div`
  font-weight: 600;
  color: #1a1f36;
`;

export const UserAccessEmail = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #5b6477;
`;

export const InlineActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const groupPermissions = (permissions: AccessControlPermission[]) =>
  permissions.reduce<Record<string, AccessControlPermission[]>>((groups, permission) => {
    const group = groups[permission.resource] ?? [];
    group.push(permission);
    groups[permission.resource] = group;
    return groups;
  }, {});

export const sortPermissions = (permissions: AccessControlPermission[]) =>
  [...permissions].sort((left, right) => {
    const leftIndex = CRUD_ORDER.indexOf(left.action);
    const rightIndex = CRUD_ORDER.indexOf(right.action);
    return (leftIndex === -1 ? 99 : leftIndex) - (rightIndex === -1 ? 99 : rightIndex);
  });

export const matchesQuery = (query: string, values: string[]) => {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;

  const haystack = values.join(' ').toLowerCase();
  return terms.every((term) => haystack.includes(term));
};

export const formatRoleName = (role: string): string => {
  const mapped = USER_ROLE_TO_NAME[role as User__Role];
  if (mapped) return mapped;

  return role
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export const formatRoleNames = (roles: string[]): string => roles.map(formatRoleName).join(', ');
